import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import {
  Avatar,
  type AvatarProps,
} from "@lytra/frontend-common/components/v2/ui/avatar";
import { GET_AGENTS } from "@/components/customer/ticket/ticketQueries";

interface PersonLook {
  src?: string;
  color?: number | null;
}

const PeopleLooks = createContext<ReadonlyMap<string, PersonLook>>(new Map());

/** Loads every colleague's picture and colour slot once, for each `PersonAvatar` below. */
export function PersonAvatarsProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery(GET_AGENTS, { errorPolicy: "all" });
  const looks = useMemo(
    () =>
      new Map(
        (data?.agents ?? []).map((agent) => [
          agent.id,
          { src: agent.avatarUrl ?? undefined, color: agent.avatarColor },
        ]),
      ),
    [data],
  );
  return <PeopleLooks.Provider value={looks}>{children}</PeopleLooks.Provider>;
}

/** A colleague's avatar: their picture, else their initials on their own colour. Someone
 * not (or no longer) in the team shows neutral initials. */
export function PersonAvatar({
  personId,
  ...props
}: AvatarProps & { personId?: string | null }) {
  const look = useContext(PeopleLooks).get(personId ?? "");
  return (
    <Avatar {...props} src={look?.src ?? props.src} colorSlot={look?.color} />
  );
}
